import BookmarkOutlinedIcon from "@mui/icons-material/BookmarkOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { Alert, Box, Button, Link, Stack } from "@mui/material";
import Lottie from "lottie-light-react";
import { type FC, useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Trans } from "react-i18next";
import { downloadFile } from "../../utils/download-files";
import star from "./../content-previews/star.json";
import { useGenerateCommsContext } from "./generate-comms-context";
import { useTemplateImageDimensions } from "./use-comms-template-image-dimensions";

export const CommsTemplateImage: FC<{ format: string }> = ({ format }) => {
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);

  const {
    saveImage,
    previewImages: { images, selectedImage },
    branding: { selectedImageType },
  } = useGenerateCommsContext();
  const loader = images?.[selectedImage];
  const dimensions = useTemplateImageDimensions(selectedImageType);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loader || loader.loading || !loader.data) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setImageUrl(undefined);
    } else {
      setImageUrl(loader.data.url);
    }
  }, [loader, setImageUrl]);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const savingToast = toast.custom(<SavingToast />, {
        duration: 3000,
      });
      await saveImage();
      setSaving(false);
      toast.dismiss(savingToast);
      toast.custom(<SuccessToast />, { duration: 2000 });
    } catch (e: unknown) {
      setSaving(false);
      toast.custom(<ErrorToast />, { duration: 2000 });
      console.log(e);
    }
  }, [saveImage]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      <Box
        data-testid="template-image"
        sx={[
          {
            position: "relative",
            borderRadius: "5px",
            border: "5px solid #eee",
            padding: "5px",
            overflow: "hidden",
            // add 20: 2 * (border + padding)
            width: dimensions.width + 20,
            height: dimensions.height + 20,
          },
        ]}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            boxSizing: "content-box",
            position: "relative",
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          data-testid="template-image-bg"
        >
          {!imageUrl && (
            <Box
              style={{
                display: "flex",
                marginTop: "8px",
                marginLeft: "8px",
              }}
            >
              <Lottie
                animationData={star}
                loop
                autoPlay
                style={{ width: "24px", height: "24px" }}
              />
            </Box>
          )}
        </Box>
      </Box>
      {format.toLowerCase() !== "communication" && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "center", sm: "flex-start" },
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<DownloadOutlinedIcon />}
            data-analytics-id="comms-image-download"
            disabled={!imageUrl}
            onClick={() => void downloadFile(imageUrl ?? "", format, "png")}
          >
            Download Image
          </Button>
          <Button
            variant="outlined"
            startIcon={<BookmarkOutlinedIcon />}
            data-analytics-id="comms-image-save"
            disabled={!imageUrl}
            loading={saving}
            onClick={() => void handleSave()}
          >
            Save to Content Library
          </Button>
        </Stack>
      )}
    </Box>
  );
};

const SuccessToast: FC = () => {
  return (
    <Alert severity="success">
      <Trans>
        Saved to your <Link href="/content-library">Content Library</Link>
      </Trans>
    </Alert>
  );
};

const SavingToast: FC = () => {
  return (
    <Alert severity="info">
      <Trans>Saving image...</Trans>
    </Alert>
  );
};

const ErrorToast: FC = () => {
  return (
    <Alert severity="error" variant="outlined">
      <Trans>There was an error saving your image. Please try again later.</Trans>
    </Alert>
  );
};
